/**
 * CatalogCardGrid — one grid of catalogue cards, drawn one way.
 *
 * Both screens that show catalogue rows draw them through here: the flat "All
 * Cards" list behind the Explore menu, and the card half of a smart search
 * result. They were two grids with two sets of styles, and the search's copy
 * had no zoom in it at all — which is exactly the complaint that produced this
 * file: "if i search i notice the cards break our zoom preference and theres no
 * way to change it". A second grid is a second place for zoom to go missing, so
 * there is now one.
 *
 * WHAT IT OWNS
 *   the three zoom steps and what each one shows
 *   the separator headings an order implies
 *   the price, the Pocket chip and the owned badge on a tile
 *
 * WHAT IT DOES NOT OWN
 *   where a tap goes. `onCardTap` is the only way a tile does anything, and it
 *   is a required prop rather than a default of "navigate". The wish pickers use
 *   that seam to SELECT cards instead of opening them — "Add N cards to
 *   <binder>" — and pass the selection down through `selectedIds`, which is why
 *   there is one grid rather than a fork per screen.
 *
 *   What a selected tile LOOKS like is owned here, though, and it has to be
 *   drawn as an element rather than as an outline. See `selectRing`.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { holdProps } from '../wish/wishHold';
import CardImage from '../CardImage';
import PrintingBadge from '../PrintingBadge';
import { TcgPocketChip } from '../TcgPocketNotice';
import { buildSetPrintingTiles } from './setPrintingTiles';
import { fetchSlotGridForCards } from '../../services/wishSlots';
import { isTcgPocketSet } from '../../utils/tcgPocket';
import { formatMoney } from '../../utils/catalogPrices';
import { catalogGroupOf } from '../../services/cardData/catalogFilters';

// Printing rows already fetched this session, by catalogue card id. Module
// level on purpose: the search grid and the flat catalogue are the same
// component mounted twice, and a Charizard looked up on one is known on both.
const slotCache = new Map();

/** Forget every printing row held for the session. For tests, and for a catalogue import. */
export function clearPrintingCache() {
  slotCache.clear();
}

/**
 * Columns and gap per zoom step.
 *
 * Step 0 is the UNCOLOURED, default state of the toolbar control and it is the
 * SMALLEST — five up, no captions, for skimming art. That direction is not
 * arbitrary: every zoom in this app reads the same way, so a person who learns
 * the button on one screen has learned it everywhere.
 */
export const ZOOM_COLUMNS = [5, 3, 2];

export function zoomGrid(zoom) {
  const level = Math.min(Math.max(Number(zoom) || 0, 0), 2);
  return {
    gridTemplateColumns: `repeat(${ZOOM_COLUMNS[level]}, minmax(0, 1fr))`,
    gap: level === 0 ? '6px' : '8px',
  };
}

const styles = {
  grid: {
    display: 'grid',
    // minmax(0, 1fr), not 1fr: a track's default min-content floor is what the
    // card art refuses to shrink below, which pushed the last column off the
    // side of the phone.
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '8px',
    width: '100%',
  },
  tile: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    background: 'transparent', border: 'none', padding: 0,
    color: 'var(--text)', textAlign: 'left', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  imgWrap: {
    position: 'relative', width: '100%', aspectRatio: '5 / 7',
    borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-sheet)',
  },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  // Sits where TcgPocketChip sits, because they answer the same question and
  // never both appear on one card.
  price: {
    position: 'absolute', bottom: '4px', right: '4px',
    padding: '2px 5px', borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    maxWidth: 'calc(100% - 8px)', overflow: 'hidden',
  },
  priceText: {
    fontSize: '9px', fontWeight: '700', color: '#4ade80',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  owned: { position: 'absolute', bottom: '4px', left: '4px', display: 'flex', gap: '4px' },
  ownedTag: {
    backgroundColor: 'var(--accent)', color: '#fff',
    fontSize: '9px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px',
  },
  /**
   * The selection ring, and why it is an element rather than an outline.
   *
   * This was `outline: 2px solid var(--accent)` with a -2px offset on the image
   * wrapper, and on the phone it disappeared the moment the art arrived: the
   * card image carries an opacity transition, WebKit promotes it to its own
   * compositing layer, and a composited child paints over an inset outline
   * drawn in the parent's layer. So the highlight was there at first paint and
   * gone a beat later, which is exactly the "it counts at the bottom but does
   * not show me a highlight" report - the selection was never lost, only its
   * ring. A sibling element with a z-index cannot lose that fight.
   */
  selectRing: {
    position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
    border: '3px solid var(--accent)', borderRadius: '8px',
    pointerEvents: 'none', zIndex: 3,
  },
  // The ring alone is thin at five tiles across, and a tick is the thing a
  // thumb is looking for when it scans a grid for what it already picked.
  selectCheck: {
    position: 'absolute', top: '4px', left: '4px',
    width: '20px', height: '20px', borderRadius: '10px',
    backgroundColor: 'var(--accent)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'none', zIndex: 4,
  },
  name: {
    fontSize: '12px', fontWeight: '650', color: 'var(--text)', lineHeight: 1.25,
    marginTop: '6px', overflow: 'hidden', textOverflow: 'ellipsis',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  meta: {
    fontSize: '10.5px', color: 'var(--text-secondary)', lineHeight: 1.25, marginTop: '2px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
  },
  groupHeader: {
    userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
    gridColumn: '1 / -1',
    display: 'flex', alignItems: 'baseline', gap: '8px',
    padding: '10px 2px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '-4px',
  },
  groupName: {
    fontSize: '13px', fontWeight: '600', color: 'var(--text)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  groupYear: { fontSize: '12px', color: 'var(--text-muted, var(--text-secondary))', flexShrink: 0 },
};

/**
 * @param {{
 *   cards: object[],
 *   zoom?: 0|1|2,
 *   groupMode?: string|null,      from catalogGrouping(sort)
 *   eras?: object[]|null,         for the dual era/set heading
 *   theme: object,
 *   isDark?: boolean,
 *   pricingEnabled?: boolean,
 *   ownedCardIds?: Set<string>|null,
 *   onCardTap: (card) => void,    the seam; no default, deliberately
 *   selectedIds?: Set<string>|null,  lifted selection, for the coming picker
 *   style?: object,               extra grid style, e.g. bottom clearance
 * }} props
 */
export default function CatalogCardGrid({
  cards = [],
  zoom = 0,
  groupMode = null,
  eras = null,
  theme,
  isDark = true,
  pricingEnabled = false,
  ownedCardIds = null,
  onCardTap,
  onCardHold = null,
  selectedIds = null,
  style = null,
  // A printing is a card everywhere, not only inside a set: the owner's rule.
  // On, a Charizard with four print runs is four tiles, each badged and each
  // quoting its own price. Off is for a surface that has a reason to show one
  // tile per card.
  printings = true,
  // Printings the query asked for, as lower-case catalogue labels. "mewtwo 10
  // shadowless" wants the Shadowless tile first, not somewhere down the grid.
  wantPrintings = null,
}) {
  const captions = zoom > 0;
  // Shared by every tile: a hold that has just fired must swallow the click the
  // browser sends after the finger lifts, or holding a card would open the
  // destination popup and then immediately act on the tap underneath it.
  const holdFired = useRef(false);

  // The printing rows for whatever cards are on screen. Remembered per card
  // for the session, so a page that arrives asks only about the cards it
  // brought, and a re-sort of the same sixty asks for nothing. Until they
  // arrive the grid draws one tile per card, so nothing waits on this.
  const idKey = cards.map((c) => c.id).join('|');
  const [slotsVersion, setSlotsVersion] = useState(0);
  useEffect(() => {
    if (!printings) return undefined;
    const missing = cards.map((c) => c.id).filter((id) => id && !slotCache.has(id));
    if (!missing.length) return undefined;
    let alive = true;
    Promise.resolve(fetchSlotGridForCards(missing))
      .then((rows) => {
        const byCard = new Map();
        for (const row of rows || []) {
          const list = byCard.get(row.card_id);
          if (list) list.push(row); else byCard.set(row.card_id, [row]);
        }
        // A card the printing model has no rows for is remembered as such,
        // rather than asked about again on every render.
        for (const id of missing) slotCache.set(id, byCard.get(id) || []);
        if (alive) setSlotsVersion((v) => v + 1);
      })
      .catch((e) => {
        console.warn('[grid] printing rows', e?.message || e);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, printings]);

  const tiles = useMemo(() => {
    if (!printings) {
      return cards.map((card) => ({ key: card.id, card, badge: null, price: null,
                                    priceIsForPrinting: false, image: card.image_url || null }));
    }
    const slots = cards.flatMap((c) => slotCache.get(c.id) || []);
    const built = buildSetPrintingTiles({ cards, slots, keepUnslotted: true });
    const want = (wantPrintings || []).map((p) => String(p).toLowerCase());
    if (!want.length) return built;
    // Asked-for printings lead, in the order the cards were already in. A
    // printing nobody asked for is still here: the query narrows the eye, not
    // the catalogue.
    const asked = (tile) => {
      const label = String(tile.badge?.full || tile.badge?.label || '').toLowerCase();
      return want.some((w) => label === w || label.includes(w));
    };
    return [...built.filter(asked), ...built.filter((t) => !asked(t))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, printings, slotsVersion, (wantPrintings || []).join('|')]);

  return (
    <div style={{ ...styles.grid, ...zoomGrid(zoom), ...(style || {}) }}>
      {tiles.map((tile, index) => {
        const card = tile.card;
        /* A label before the first card of each group.
           Derived from the whole accumulated list, not from the page that just
           arrived, which is what makes it survive paging: the previous card is
           simply the one before it in the array, whether it came back in this
           page or six pages ago. So a heading is emitted only on a genuine
           change, never repeated at the top of each page, and never missed at a
           boundary that falls mid-page. It also cannot end up as the last thing
           on screen with nothing under it, because it is only ever emitted
           immediately before a card that is already here. */
        let header = null;
        if (groupMode) {
          const group = catalogGroupOf(groupMode, card, eras);
          const prev = index > 0 ? tiles[index - 1].card : null;
          const prevKey = prev ? catalogGroupOf(groupMode, prev, eras).key : null;
          if (group.key !== prevKey) {
            header = (
              <div key={`g-${group.key}`} style={styles.groupHeader}>
                <span style={styles.groupName}>{group.label}</span>
                {group.meta && <span style={styles.groupYear}>{group.meta}</span>}
              </div>
            );
          }
        }

        const selected = selectedIds ? selectedIds.has(card.id) : false;
        // This printing's price when it has one, the card's when it does not,
        // and a tilde on the second so the two are never confused.
        const priceText = tile.price != null
          ? `${tile.priceIsForPrinting ? '' : '~'}${formatMoney(tile.price)}`
          : formatMoney(card.near_mint);

        return (
          <Fragment key={tile.key}>
            {header}
            <button
              style={styles.tile}
              aria-pressed={selectedIds ? selected : undefined}
              onClick={() => {
                if (holdFired.current) return;
                // The printing goes through the door with the card. Without it
                // every printing of a Charizard opened the same screen.
                if (onCardTap) onCardTap(card, tile.badge || null);
              }}
              {...(onCardHold
                ? holdProps({ onHold: () => onCardHold(card), firedRef: holdFired })
                : {})}
              onContextMenu={(ev) => {
                // The desktop and long-press-accessibility equivalent of the
                // same gesture, so the feature is not touch-only.
                if (!onCardHold) return;
                ev.preventDefault();
                onCardHold(card);
              }}
            >
              <div style={styles.imgWrap}>
                <CardImage
                  /* This printing's picture, not the card's. A 1st Edition
                     Charizard and an Unlimited one are different photographs
                     and very different money; drawing one for both is the bug
                     this grid had. Where a printing shares the card's picture
                     - plain holo, reverse holo - the tile gets that same URL,
                     which is correct rather than a fallback. */
                  digitalImage={tile.image || card.image_url || null}
                  name={card.name}
                  number={card.local_id}
                  setName={card.set_name || card.set_id}
                  imgStyle={styles.img}
                  theme={theme}
                  loading="lazy"
                  thumb
                />
                {/* A mixed list, so the badge earns its place here. Inside a
                    Pocket set's own grid it would be 331 repetitions of a fact
                    the header already states. */}
                {isTcgPocketSet(card.set_id) && <TcgPocketChip theme={theme} isDark={isDark} small />}
                {/* Which printing this tile is. Four identical Charizards are
                    told apart by nothing else, and the gap between two of them
                    is the gap between forty dollars and four hundred. */}
                {tile.badge && (
                  <PrintingBadge
                    subtype={tile.badge.subtype}
                    stamps={tile.badge.stamps}
                    foil={tile.badge.foil}
                    type={tile.badge.type}
                    short
                    style={{ position: 'absolute', top: '6px', left: '6px' }}
                  />
                )}
                {/* The price rides in with the row: this printing's own where
                    it has one, the card's NEAR_MINT figure from catalog_browse
                    where it does not, so a card and its set never quote
                    different money. */}
                {pricingEnabled && !isTcgPocketSet(card.set_id) && priceText && (
                  <div style={styles.price}>
                    <span style={styles.priceText}>{priceText}</span>
                  </div>
                )}
                {selected && (
                  <>
                    <div
                      style={styles.selectRing}
                      data-testid={`selected-${card.id}`}
                      aria-hidden="true"
                    />
                    <div style={styles.selectCheck} aria-hidden="true">
                      <Check size={13} strokeWidth={3.5} />
                    </div>
                  </>
                )}
                {ownedCardIds && ownedCardIds.has(card.id) && (
                  <div style={styles.owned}>
                    <span style={styles.ownedTag}>O</span>
                  </div>
                )}
              </div>
              {/* At the smallest size the captions are dropped, the way the All
                  tab drops them at its own smallest size. Five tiles to a row
                  leaves no width for a two-line name and a set line, and the
                  point of this size is to skim the art. */}
              {captions && (
                <>
                  <span style={styles.name}>{card.name}</span>
                  <span style={styles.meta}>
                    {card.set_name || card.set_id} · {card.local_id}
                  </span>
                </>
              )}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}
